import { getAsync } from "@/helpers/apiHelper";
import { AuthProviderType } from "@/models/enums";
import store from "@/store";
import Router from "../main.router";
import jwt_decode from "jwt-decode";

const endpoint = process.env.VUE_APP_BACKEND + "/api/account";
const host = process.env.VUE_APP_BACKEND;
const externalLoginEndpoint = process.env.VUE_APP_BACKEND + "/api/ExternalLogin/LoginAdfs/";

class AuthService {
  constructor() {
    this.allowedOrigins = [];
    this.providers = {};
    this.appRedirect = () => null;
    this.authenticatedCallbacks = [];
    this.signedOutCallbacks = [];
    this.identity = null;
    this.accessToken = null;
    this.tokenExpiresAt = null;
    this.loginRedirectUrl = null;
    this.claims = null;
  }

  // Return boolean of if we are currently connected
  get isConnected() {
    // check in the store if the user is currently logging out
    var isLogginggOut = store.getters.getIsLoggingOut;
    // Check the expiry moment of the current token. If expired, and not logging out, display a snackbar to alert about the session timeout
    if (this.tokenExpiresAt != null && this.tokenExpiresAt <= new Date()) {
      // If the token is expired, remove the identity
      this.identity = null;

      if (!isLogginggOut) {
        store.dispatch("setSnackbar", {
          status: "session_timeout",
        });
      }
    }
    // We wanna alert the user if his session will expires in less than 15 minutes, if the user is not logging out
    else if (!isLogginggOut && this.tokenExpiresAt != null) {
      var tokenExpiresAtTime = this.tokenExpiresAt.getTime();
      var nowTime = new Date().getTime();
      const diffMs = tokenExpiresAtTime - nowTime;
      const diffMins = Math.floor(diffMs / 1000 / 60);
      if (diffMins <= 15) {
        store.dispatch("setSnackbar", {
          status: "session_timeout_in_less_than_X_minutes",
          minutes: diffMins > 0 ? diffMins.toString() : "1",
        });
      }
    }

    // double check with access_token
    let token = store.getters.getAccessToken;

    // If connected
    if (this.identity != null && token && token !== "undefined" && token !== "null") {
      return true;
    }
    // else not connected
    else {
      return false;
    }
  }

  // Get the current email from claims
  get mail() {
    var identity = this.identity;

    return identity ? identity.email : null;
  }

  // Get the current role from claims TODO: roles now
  get role() {
    // var identity = this.identity;
    // return identity ? identity.role : null;
    return this.roles && this.roles[0] ? this.roles[0] : null;
  }

  // Get the current roles from claims
  get roles() {
    var identity = this.identity;
    return identity ? identity.roles : null;
  }

  // Get the current federations from claims
  get federations() {
    var identity = this.identity;
    return identity ? identity.federations : null;
  }

    // Get the current disciplines from claims
    get disciplines() {
      var identity = this.identity;
      return identity ? identity.disciplines : null;
    }

  // Get the current user id from claims
  get userId() {
    var identity = this.identity;
    return identity ? identity.id : null;
  }

  // Get the current federation from claims
  get federation() {
    const { identity } = this;

    return identity ? identity.federations[0] : null;
  }

  // Get the current roles from claims
  get permissions() {
    return this.claims;
  }

  // Triggered when the application launch
  async init() {
    // Pass a flag to onAuthenticatedFromIdentity in order to avoid to display loggedin snackbar when we hit F5 (because the token is stored in memory and not in session storage .............)
    var comeFromInit = true;
    // let token = await this.getToken();
    // if (token !== null) await this.onAuthenticatedFromIdentity(token, comeFromInit);

    // Check if there is a token in the store from local storage
    store.dispatch("fetchAccessToken");
    store.dispatch("fetchTokenExpiresIn");
    store.dispatch("fetchRefreshToken");

    let token = store.getters.getAccessToken;

    if (token && token !== "undefined" && token !== "null") await this.onAuthenticatedFromIdentity(comeFromInit);
    else this.refreshToken();
  }

  // Refresh token
  async refreshToken() {
    var result = await this.refreshTokenFlow();

    if (result) {
      return await this.handleAuthFlow(result, true);
    } else {
      // if it didn't worked we wanna logout without logout message (session expired message)
      this.logout(false);
    }
  }

  // Try to login using one of the available providers
  async login(selectedProvider, credentials = null) {
    var result = false;

    switch (selectedProvider) {
      case AuthProviderType.IDENTITY_SERVER:
        result = await this.loginUsingIdentityServer(credentials);
        break;
      case AuthProviderType.ADFS:
        this.initiateLoginThroughAdfs();
        break;
      default:
        break;
    }

    return result;
  }

  // Try to login using IdentityServer internal auth
  async loginUsingIdentityServer(credentials) {
    var result = await this.passwordFlowAuthenticate(credentials);

    return this.handleAuthFlow(result);
  }

  // Authenticate the user with its login and password
  // Return the access_token
  async passwordFlowAuthenticate(credentials) {
    var url = `${host}/connect/token`;

    var payload = {
      grant_type: "password",
      client_id: "ene_app_api",
      username: credentials.username,
      password: credentials.password,
    };

    let xWwwFormUrlEncodedPayload = new URLSearchParams(Object.entries(payload)).toString();

    let result = false;

    try {
      store.dispatch("addRemoveRequest", true);

      result = await fetch(url, {
        method: "POST",
        mode: "cors",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: xWwwFormUrlEncodedPayload,
      });
    } catch (ex) {
      store.dispatch("setSnackbar", {
        title: "Erreur",
        text: "Le serveur n'est pas accessible.",
        color: "error",
      });
    }

    store.dispatch("addRemoveRequest", false);

    if (result === false) {
      return;
    }

    // If OK return the token
    if (result.ok) {
      let token = await result.json();
      return token;
    }
    // Else display an authentification error
    else {
      store.dispatch("setSnackbar", {
        title: "Echec d'authentification",
        text: "Email et/ou mot de passe invalide.",
        status: "error",
        timeout: 2000,
      });
    }

    return null;
  }

  // Refresh the token
  async refreshTokenFlow() {
    var url = `${host}/connect/token`;

    // Check if there is a token in the store from local storage
    store.dispatch("fetchRefreshToken");

    var refreshToken = store.getters.getRefreshToken;

    if (refreshToken) {
      var payload = {
        grant_type: "refresh_token",
        client_id: "ene_app_api",
        refresh_token: refreshToken,
      };

      let xWwwFormUrlEncodedPayload = new URLSearchParams(Object.entries(payload)).toString();

      let result = false;

      try {
        store.dispatch("addRemoveRequest", true);

        result = await fetch(url, {
          method: "POST",
          mode: "cors",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: xWwwFormUrlEncodedPayload,
        });
      } catch (ex) {
        store.dispatch("setSnackbar", {
          title: "Erreur",
          text: "Le serveur n'est pas accessible.",
          color: "error",
        });
      }

      store.dispatch("addRemoveRequest", false);

      if (result === false) {
        return;
      }

      // If OK return the token
      if (result.ok) {
        let token = await result.json();
        return token;
      }
    }

    return null;
  }

  // Logout from every authentication provider
  logout(shouldDisplayLogoutMessage = true) {
    this.onSignedOut(shouldDisplayLogoutMessage);
  }

  // Signout
  onSignedOut(shouldDisplayLogoutMessage = true) {
    // Save it in store and in local storage
    store.dispatch("saveAccessToken", null);
    store.dispatch("saveTokenExpiresIn", null);
    store.dispatch("saveRefreshToken", null);

    // Save in the store we are currently logging out (in order to prevent session_timeout messages)
    store.dispatch("setIsLoggingOut", true);

    if (shouldDisplayLogoutMessage) {
      // Display snackbar about user logging out
      store.dispatch("setSnackbar", {
        status: "user_loggedout",
      });
    }

    this.identity = null;

    for (var cb of this.signedOutCallbacks) {
      cb();
    }
  }

  // On authenticated from Identity Server
  async onAuthenticatedFromIdentity(comeFromInit = false) {
    let access_Token = store.getters.getAccessToken;
    let refresh_Token = store.getters.getRefreshToken;
    let expires_In = store.getters.getTokenExpiresIn;

    // Set the access token
    //this.accessToken = access_Token;

    // Tranform the expires in value into a date time in order to keep track of token expiry without calling the backend.
    this.tokenExpiresAt = new Date(new Date().getTime() + expires_In * 1000);

    // Set is logging out to false in order to enable session_timeout snackbar if required
    store.dispatch("setIsLoggingOut", false);

    const profile = await this.fetchProfile(access_Token);
    this.identity = { ...access_Token, ...profile };
    
    // Exrtact the permissions from the token for later use
    let decodedJwt = jwt_decode(access_Token);
    this.claims = decodedJwt.permission;

    // Send welcome message snackbar if we actually just loggedin
    if (!comeFromInit) {
      store.dispatch("setSnackbar", {
        status: "welcome_message",
        username: profile.email,
      });
    }

    for (var cb of this.authenticatedCallbacks) {
      cb();
    }

    // Redirect to dashboard if not come from init
    if (!comeFromInit) {
      // TODO: TEST WITHOUT BECAUSE IT IS BREAKING REDIRECT AFTER LOGIN
      //Router.push({ name: "Dashboard" });
    }
  }

  // Get the user info from backend
  async fetchProfile() {
    const { token, ...profile } = (await getAsync(`${endpoint}/users/me`)) || {};

    return profile;
  }

  // Register on authenticated callback
  registerAuthenticatedCallback(cb) {
    this.authenticatedCallbacks.push(cb);
  }

  // Remove the registering off authenticated callback
  removeAuthenticatedCallback(cb) {
    this.authenticatedCallbacks.splice(this.authenticatedCallbacks.indexOf(cb), 1);
  }

  // Register on signed out callback
  registerSignedOutCallback(cb) {
    this.signedOutCallbacks.push(cb);
  }

  // Remove the signed out off authenticated callback
  removeSignedOutCallback(cb) {
    this.signedOutCallbacks.splice(this.signedOutCallbacks.indexOf(cb), 1);
  }

  // Handle return of auth flow
  async handleAuthFlow(result, isRefresh = false) {
    // If the authentication worked and if we have an access_token
    if (result) {
      // Save it in store and in local storage
      store.dispatch("saveAccessToken", result.access_token);
      store.dispatch("saveTokenExpiresIn", result.expires_in);
      store.dispatch("saveRefreshToken", result.refresh_token);

      await this.onAuthenticatedFromIdentity(isRefresh);

      return true;
    } else {
      //Save it in store and in local storage
      store.dispatch("saveAccessToken", null);
      store.dispatch("saveTokenExpiresIn", null);
      store.dispatch("saveRefreshToken", null);

      return false;
    }
  }

  // Call the endpoint that initiate the authentication through ADFS
  initiateLoginThroughAdfs() {
    // Redirect the browser to the url get from the backend
    window.location.href = `${externalLoginEndpoint}`;
  }

  // Handle auth from the adfs redirect from the backend
  async processLoginResponseFromAdfsRedirect(response) {
    await this.handleAuthFlow(response);

    // const accessToken = response.access_token;

    // if (accessToken == null) {
    //   throw new Error('accessToken cannot be null');
    // }

    // const refreshToken = response.refresh_token || this.refreshToken;
    // const expiresIn = response.expires_in;
    // const tokenExpiryDate = new Date();
    // tokenExpiryDate.setSeconds(tokenExpiryDate.getSeconds() + expiresIn);
    // const accessTokenExpiry = tokenExpiryDate;
    // const jwtHelper = new JwtHelper();
    // const decodedAccessToken = jwtHelper.decodeToken(accessToken) as AccessToken;

    // const permissions: PermissionValues[] = Array.isArray(decodedAccessToken.permission) ? decodedAccessToken.permission : [decodedAccessToken.permission];

    // if (!this.isLoggedIn) {
    //   this.configurations.import(decodedAccessToken.configuration);
    // }

    // const user = new User(
    //   decodedAccessToken.sub,
    //   decodedAccessToken.name,
    //   decodedAccessToken.fullname,
    //   decodedAccessToken.email,
    //   Array.isArray(decodedAccessToken.role) ? decodedAccessToken.role : [decodedAccessToken.role],
    //   decodedAccessToken.facilityName, decodedAccessToken.facilityId);
    // user.isEnabled = true;

    // this.saveUserDetails(user, permissions, accessToken, refreshToken, accessTokenExpiry, rememberMe);

    // this.reevaluateLoginStatus(user);

    // return user;
  }

  // Redirect the browser after login through ADFS witht the provided redirect url
  redirectLoginUserWithoutParams() {
    const redirect = this.loginRedirectUrl && this.loginRedirectUrl != "/" && this.loginRedirectUrl != "/hub" ? this.loginRedirectUrl : "/hub";

    Router.push({ name: redirect });
  }
}

export default new AuthService();
